import { PAGE_INTENT } from "@/src/common/business-layer/constants"
import { Fallback } from "@/src/common/components/Fallback"
import Link from "@/src/common/components/Link"
import useClearSession from "@/src/common/hooks/useClearSession"
import { parseSearchParam, snakeCaseObjectProperty } from "@/src/common/utils"
import { SB_MEMBER_PROFILE_API_URL } from "@/src/sb/business-layer/config"
import { LoggedInUser } from "@/src/sb/business-layer/types"
import { useAnalytics } from "@/src/sb/components/Analytics"
import { AnalyticsService } from "@/src/sb/components/Analytics/AnalyticsService"
import { SEOProps } from "@/src/sb/components/SEO/settings"
import SigninForm from "@/src/sb/components/auth/SigninForm"
import { AuthLayout } from "@/src/sb/components/layouts/AuthLayout"
import { DATA_TRACKER } from "@/src/sb/constants"
import { V2_FEATURE_TOGGLES } from "@/src/sb/constants/configs"
import { pages } from "@/src/sb/constants/copy"
import { PLATFORMISATION } from "@/src/sb/constants/featureFlags"
import { Auth } from "@/src/sb/core/api/Auth"
import { SignInData } from "@/src/sb/core/models/auth"
import { filterObject } from "@/src/sb/core/utils/helpers"
import { validateData } from "@/src/sb/core/utils/validate"
import { SigninSchema } from "@/src/sb/core/validatorSchemas/signin"
import useRequestDemo from "@/src/sb/hooks/useRequestDemo"
import { useFeatureIsOn } from "@growthbook/growthbook-react"
import * as Sentry from "@sentry/nextjs"
import { getCsrfToken, getSession, signIn, useSession } from "next-auth/react"
import { useRouter } from "next/router"
import React, { ChangeEvent, useEffect, useState } from "react"

import ExternalHTTPInstance from "../../common/data-layer/datasources/ExternalHTTP"

const isEmpty = (obj: object) => !Object.values(obj).map(Boolean).includes(true)

type SigninErrors = {
  username?: [string]
  password?: [string]
}

type AuthError = {
  email?: string
  errors?: SigninErrors
  message?: string
  code?: "no_such_account" | "account_not_active" | string
}

type SigninProps = {
  seo?: SEOProps
}

export async function persistCorporateInfoForTracking(
  analytics: AnalyticsService
) {
  const session = await getSession()
  let memberStatus = "free" // Default

  const token = (session?.user as any)?.accessToken as string
  const userInformation = token
    ? await ExternalHTTPInstance.get<LoggedInUser["data"]>(
        SB_MEMBER_PROFILE_API_URL,
        token
      )
    : undefined

  const licenceCategory = userInformation?.corporate_subscriber_licence
  if (userInformation?.roles.includes("premium") && !licenceCategory) {
    memberStatus = "individual_subscriber"
  }

  if (licenceCategory) {
    memberStatus = "corporate_user"
  }

  // register corporate information
  if (userInformation) {
    analytics.getAnalytics()?.setPersistentGlobalEventProps(
      snakeCaseObjectProperty({
        memberStatus,
        corporateId: userInformation.corporate_subscriber_id
      })
    )
  }
}

const SignIn = () => {
  useClearSession() // clear session only config, clean slate
  // Boolean on/off features
  const platformisation = useFeatureIsOn("platformisation")
  const [showDataTrackerIntent, setShowDataTrackerIntent] = useState(false)

  const router = useRouter()
  const session = useSession()
  const { next: nextRoute } = router.query
  const analytics = useAnalytics()
  const [loading, setLoading] = useState(false)
  const [changedFields, setChangedFields] = useState<Array<string>>([])

  const [initialErrors, setInitialErrors] = useState<AuthError | null>()

  const [formErrors, setFormErrors] = useState<SigninErrors | null>(
    initialErrors?.errors || null
  )

  const [formData, setFormData] = useState<SignInData>({
    username: "",
    password: "",
    csrfToken: ""
  })

  const requestDemo = useRequestDemo()

  useEffect(() => {
    if (session.status === "authenticated") {
      Auth.signout({
        callbackUrl: `/accounts/signin${
          router.query?.next ? `?next=${router.query?.next as string}` : ""
        }`
      })
    }
  }, [session.status])

  useEffect(() => {
    if (initialErrors) {
      analytics.trackLoginFailure({
        code: initialErrors.code,
        message: initialErrors.message,
        errors: initialErrors.errors
      })
    }
  }, [initialErrors, analytics])

  useEffect(() => {
    if (!router.isReady) return

    if (router.query.intent === DATA_TRACKER) {
      setShowDataTrackerIntent(true)
    }
  }, [router])

  if (session.status === "authenticated" || session.status === "loading") {
    return <Fallback logo={false} />
  }

  const handleChange = (e: ChangeEvent) => {
    const { name, value } = e.target as any
    const updatedFormData = { ...formData, [name]: value }
    setChangedFields(Array.from(new Set([...changedFields, name])))
    const [, errors] = validateData(updatedFormData, SigninSchema)

    const dirtyFieldErrors = filterObject(errors as any, changedFields)

    setFormErrors(dirtyFieldErrors)
    setFormData(updatedFormData)
  }

  const getCsrfTokenUsingNextAuthHelper = async (): Promise<string> => {
    let token
    try {
      token = await getCsrfToken()
    } catch (e) {
      Sentry.captureException(e)
    }

    return token as string
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    const csrfToken = await getCsrfTokenUsingNextAuthHelper()

    const [, errors] = validateData(formData, SigninSchema)
    if (errors) {
      setFormErrors(errors as any)
    } else {
      const pageIntent = PAGE_INTENT[nextRoute as string]

      try {
        await Auth.signIn({ ...formData, json: true, csrfToken })

        await persistCorporateInfoForTracking(analytics)

        const signInUrlQueriesWithoutNext = parseSearchParam({
          auth_type: "password_login"
        }).toString()

        // forward queries (could be UTM tags)
        window.location.href =
          nextRoute && nextRoute?.indexOf("signout") === -1
            ? (nextRoute as string) +
              (pageIntent
                ? `?${parseSearchParam({
                    intent: pageIntent
                  }).toString()}`
                : "")
            : `${
                platformisation ?? V2_FEATURE_TOGGLES.has(PLATFORMISATION)
                  ? "/search/"
                  : "/home/"
              }?${signInUrlQueriesWithoutNext}` // default route on sign-in
      } catch (e) {
        const error = e as any
        if (error.response.status === 401) {
          const errorJSON = parseSearchParam(error.response.data.url).get(
            "error"
          )
          setInitialErrors(parseRouteError(errorJSON as string))
        }
        setLoading(false)
      }
    }
  }

  const formIsValid = () => {
    const [, errors] = validateData(formData, SigninSchema)
    return !isEmpty(formData) && errors === null
  }

  const handleSocialLogin = (platform: "apple" | "google") => async () => {
    analytics.trackLogin(platform)
    await signIn(platform, {
      redirect: false,
      callbackUrl: router.query.next
        ? `${pages.socialRegistrationSuccess}?next=${router.query.next}`
        : pages.socialRegistrationSuccess
    })
  }

  const handleResendVerification = async () => {
    try {
      await Auth.resendVerification(formData.username)
      router.push(pages.waitForActivate)
    } catch (error) {
      console.warn("Network errors")
    }
  }

  return (
    <AuthLayout>
      <section className={"!max-w-[400px]"}>
        <SigninForm
          headerAddOn={
            showDataTrackerIntent ? (
              <p
                className={
                  "text-accent-800 bg-accent-100 px-3 py-2 my-8 text-center text-xs font-light"
                }
              >
                You need a{" "}
                <Link
                  href={requestDemo.url}
                  className={"underline"}
                  onClick={() =>
                    requestDemo.trackClickCorporateQuote({
                      context: "data_tracker_intent"
                    })
                  }
                >
                  Corporate Licence
                </Link>{" "}
                to access this data.
              </p>
            ) : null
          }
          signinDisabled={!formIsValid()}
          signupLink={`${requestDemo.url}?${parseSearchParam(
            router.query
          ).toString()}`}
          handleSubmit={handleSubmit}
          loading={loading}
          errors={formErrors}
          formErrorMsg={initialErrors?.message}
          formErrorCode={initialErrors?.code}
          resendVerification={handleResendVerification}
          handleChange={handleChange}
          handleGoogleLogin={handleSocialLogin("google")}
          handleAppleLogin={handleSocialLogin("apple")}
        />
      </section>
    </AuthLayout>
  )
}

export const parseRouteError = (errorStr?: string) => {
  try {
    return JSON.parse(errorStr || "")
  } catch (error) {
    return errorStr || null
  }
}

export async function getStaticProps() {
  return {
    props: {
      seo: { title: "Sign in - Stears Platform" }
    } as SigninProps // will be passed to the page component as props
  }
}

export default SignIn
